/**
 * AddToCalendarButton — split button for adding a meeting invite. The main part
 * adds to the email arche's invite calendar (Email settings → Calendar invites,
 * resolved server-side; else the default calendar); the chevron opens a menu to
 * send THIS invite to a different calendar instead.
 */

import { useState } from 'react';
import { CalendarPlus, Check, ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useInviteCalendars } from '../hooks/useInviteCalendars';
import { useEmailUserSettings } from '../hooks/useEmailUserSettings';

interface Props {
  /** No argument = the invite calendar; an id = this invite only. */
  onAdd: (calendarId?: string) => void;
  disabled?: boolean;
  size?: 'sm' | 'default';
}

export function AddToCalendarButton({ onAdd, disabled, size = 'sm' }: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  // Only fetched once the menu is opened — most adds use the saved calendar.
  const calendars = useInviteCalendars(menuOpen);
  const { inviteCalendarId } = useEmailUserSettings();
  const height = size === 'sm' ? 'h-8' : 'h-9';

  return (
    <div className="inline-flex shrink-0 self-start">
      <Button
        variant="outline"
        size={size}
        className={`${height} gap-1 rounded-r-none`}
        disabled={disabled}
        onClick={() => onAdd()}
      >
        <CalendarPlus className="h-3.5 w-3.5" aria-hidden /> Add to calendar
      </Button>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size={size}
            className={`${height} rounded-l-none border-l-0 px-2`}
            disabled={disabled}
            aria-label="Choose a calendar for this invite"
          >
            <ChevronDown className="h-3.5 w-3.5" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuLabel>Add this invite to…</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {calendars.isLoading ? (
            <div className="flex justify-center py-2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : calendars.isError ? (
            <p className="px-2 py-1.5 text-sm text-destructive">Couldn’t load your calendars</p>
          ) : (calendars.data ?? []).length === 0 ? (
            <p className="px-2 py-1.5 text-sm text-muted-foreground">
              No calendars yet — Add to calendar creates one.
            </p>
          ) : (
            (calendars.data ?? []).map((c) => (
              <DropdownMenuItem key={c.id} onSelect={() => onAdd(c.id)} className="gap-2">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: c.color }}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate">{c.name}</span>
                {c.id === inviteCalendarId && (
                  <Check className="h-3.5 w-3.5 shrink-0" aria-label="Invite calendar" />
                )}
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
